import { useEffect, useState, type ReactNode } from "react";
import { formatFileSize, type Language } from "@talentkontor/domain";
import { ApiError } from "../../../data/api";
import { documentError, sha256OfBlob } from "./dokument";
import { saveFile } from "./ausfertigung";
import { currentLang } from "../../../lib/sprache";

/**
 * PDF der Marke anzeigen (0071): Datei einmal laden, Prüfwert nachrechnen, eingebettet zeigen (object mit Blob-URL) und
 * zusätzlich „PDF öffnen“ und „Herunterladen“, weil mobile Browser eingebettete PDFs oft nicht oder nur die erste Seite
 * zeigen. Genutzt in Editor, Detail, Signierseite und Creator-Portal (Texte je Sprache und Anrede). Ohne ausdrückliche
 * Sprache gilt die Oberflächensprache (SICHT-AGENTUR-REST-25: die Agentur sah die Texte sonst immer deutsch).
 */

export const DOKUMENT_TEXT: Record<Language, {
  title: string; open: string; download: string; loading: string; ok: string; mismatch: string; size: string; file: string;
  /** im Entwurf: Vergleich mit dem angemeldeten (hochgeladenen) Dokument, versendet ist noch nichts */
  okDraft: string; mismatchDraft: string;
  embedHint: { sie: string; du: string }; error: string;
}> = {
  de: {
    title: "Dokument der Marke", open: "PDF öffnen", download: "PDF herunterladen", loading: "Dokument wird geladen …",
    ok: "✓ Prüfwert stimmt mit dem versendeten Dokument überein", mismatch: "! Prüfwert weicht ab: Diese Datei ist nicht das versendete Dokument",
    okDraft: "✓ Prüfwert stimmt mit dem hochgeladenen Dokument überein", mismatchDraft: "! Prüfwert weicht ab: Diese Datei ist nicht das hochgeladene Dokument",
    size: "Größe", file: "Datei",
    embedHint: { sie: "Ihr Browser zeigt das PDF hier nicht an. Öffnen Sie es über „PDF öffnen“ oder laden Sie es herunter.", du: "Dein Browser zeigt das PDF hier nicht an. Öffne es über „PDF öffnen“ oder lade es herunter." },
    error: "Das Dokument ließ sich nicht laden.",
  },
  en: {
    title: "Brand's document", open: "Open PDF", download: "Download PDF", loading: "Loading document …",
    ok: "✓ Checksum matches the document that was sent", mismatch: "! Checksum differs: this file is not the document that was sent",
    okDraft: "✓ Checksum matches the uploaded document", mismatchDraft: "! Checksum differs: this file is not the uploaded document",
    size: "Size", file: "File",
    embedHint: { sie: "Your browser does not display the PDF here. Open it with “Open PDF” or download it.", du: "Your browser does not display the PDF here. Open it with “Open PDF” or download it." },
    error: "The document could not be loaded.",
  },
};

export interface DokumentStand {
  url: string | null;
  sha256: string | null;
  /** stimmt der nachgerechnete Prüfwert mit dem erwarteten überein? */
  verified: boolean;
  error: string | null;
}

/** Datei laden, Blob-URL verwalten, Prüfwert nachrechnen. `loadKey` wechselt, wenn eine andere Datei gemeint ist. */
export function useDokument(load: (() => Promise<Blob>) | null, loadKey: string, expected: string | null, lang: Language = currentLang()): DokumentStand & { blob: Blob | null } {
  const [state, setState] = useState<DokumentStand & { blob: Blob | null }>({ url: null, sha256: null, verified: false, error: null, blob: null });
  useEffect(() => {
    if (!load) return;
    let cancel = false;
    let url: string | null = null;
    setState({ url: null, sha256: null, verified: false, error: null, blob: null });
    void (async () => {
      try {
        const blob = await load();
        const sha = await sha256OfBlob(blob);
        if (cancel) return;
        url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setState({ url, sha256: sha, verified: !!expected && sha === expected, error: null, blob });
      } catch (e) {
        if (cancel) return;
        const hint = e instanceof ApiError ? e.hint : undefined;
        setState({ url: null, sha256: null, verified: false, error: documentError(hint, lang) ?? (e instanceof Error && e.message ? e.message : DOKUMENT_TEXT[lang].error), blob: null });
      }
    })();
    return () => {
      cancel = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, expected]);
  return state;
}

export function DokumentAnsicht({ stand, name, size, expected, lang = currentLang(), form = "sie", title, sent = true, children }: {
  stand: DokumentStand & { blob: Blob | null };
  name: string;
  size: number | null;
  expected: string | null;
  lang?: Language;
  form?: "sie" | "du";
  title?: string;
  /** schon versendet? Im Entwurf vergleicht die Ansicht mit dem hochgeladenen, nicht mit einem versendeten Dokument. */
  sent?: boolean;
  children?: ReactNode;
}) {
  const t = DOKUMENT_TEXT[lang];
  return (
    <section className="panel stack tk-dokument" aria-label={title ?? t.title}>
      <h2>{title ?? t.title}</h2>
      <p>{t.file}: <strong>{name}</strong>{size ? ` · ${t.size} ${formatFileSize(size, lang)}` : ""}</p>
      {expected && <p className="tk-mono">SHA-256: {expected}</p>}
      {stand.error && <p className="tk-error" role="alert">{stand.error}</p>}
      {!stand.error && !stand.url && <p role="status">{t.loading}</p>}
      {stand.url && (
        <>
          {/* Prüfergebnis ist ein Satz: als Prüfpunkt (tk-checks) wie die Prüfhinweise der Rechnung */}
          <p className="tk-checks"><span className={`pill ${stand.verified ? "success" : "error"}`}>{stand.verified ? (sent ? t.ok : t.okDraft) : (sent ? t.mismatch : t.mismatchDraft)}</span></p>
          <object className="tk-pdf-frame" data={stand.url} type="application/pdf" aria-label={`${t.title}: ${name}`}>
            <p className="notice">{t.embedHint[form]}</p>
          </object>
          <div className="row">
            <a className="btn" href={stand.url} target="_blank" rel="noopener noreferrer">{t.open}</a>
            <button type="button" onClick={() => stand.blob && saveFile(stand.blob, name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`)}>{t.download}</button>
          </div>
        </>
      )}
      {children}
    </section>
  );
}
